// All domain types are sourced from @rentflowdev/api (single source of truth from
// Prisma schema). This file keeps only UI-only types and label / key maps.

// ── Re-exports from API package ──────────────────────────
export type {
	// account / user
	User,
	Account,
	AccountType,
	TaxRegime,
	UsnIncomeBand,
	AccountRole,
	SignatoryBasis,
	Signatory,
	CompanyProfile,
	CompanyDocumentKind,
	SubAccount,
	AccessEventEntry,
	SubAccountRole,
	RoleSummary,
	RoleDefinition,
	RoleGrant,
	RoleVocabularyEntry,
	SaveRoleBody,
	CreateRoleBody,
	SubAccountStatus,
	SubAccountProperty,
	InviteSubAccountBody,
	UpdateSubAccountBody,
	AssignSubAccountPropertyBody,
	ListSubAccountsParams,
	Organization,
	OrganizationSegment,
	// property
	Property,
	PropertyImage,
	PropertyUtility,
	PropertyAdditionalService,
	PropertyManager,
	PropertyPublication,
	HousingType,
	PropertyType,
	PropertyStatus,
	BuildingType,
	Renovation,
	Bathroom,
	RentPeriod,
	ServiceProvisionType,
	InvoiceSortBy,
	SortOrder,
	VatHint,
	VatReason,
	VatDecision,
	UtilityType,
	UtilityCalculation,
	UtilityPaidBy,
	RentTerms,
	ListPropertiesParams,
	ListPropertiesResponse,
	CreatePropertyBody,
	UpdatePropertyBody,
	UpdatePropertyStatusBody,
	UpsertRentTermsBody,
	UpsertUtilityBody,
	CreateAdditionalServiceBody,
	UpdateAdditionalServiceBody,
	CreatePropertyImageInput,
	// contact
	Contact,
	ContactNote,
	ContactType,
	ContactReliability,
	TenantType,
	ContactKyc,
	ListContactsParams,
	ListContactsResponse,
	CreateContactBody,
	UpdateContactBody,
	UpdateContactStatusBody,
	CreateContactNoteBody,
	UpsertContactKycBody,
	// deal
	Deal,
	DealStage,
	DealStatus,
	DealMessage,
	DealMeeting,
	DealMeetingStatus,
	DealNote,
	DealTask,
	DealTaskType,
	DealHistoryEntry,
	DealHistoryAction,
	MessageChannel,
	MessageDirection,
	ListDealsParams,
	ListDealsResponse,
	CreateDealBody,
	UpdateDealBody,
	UpdateDealStageBody,
	UpdateDealStatusBody,
	ListDealMessagesParams,
	ListDealMessagesResponse,
	CreateDealMessageBody,
	ListDealMeetingsParams,
	ListDealMeetingsResponse,
	CreateDealMeetingBody,
	UpdateDealMeetingBody,
	// rental
	Rental,
	RentalStatus,
	PaymentStatus,
	RentalPeriodicity,
	RentalNote,
	RentalTask,
	RentalTaskType,
	RentalHistoryEntry,
	RentalHistoryAction,
	RentalAutomation,
	AutomationInvoiceFormation,
	AutomationSendMode,
	AutomationActFormation,
	AutomationAccrualDistribution,
	AutomationPaymentDistribution,
	ListRentalsParams,
	ListRentalsResponse,
	CreateRentalBody,
	StartRentalBody,
	StartRentalContact,
	StartRentalTerms,
	StartRentalWarning,
	StartRentalResponse,
	UpdateRentalBody,
	UpdateRentalStatusBody,
	ListRentalNotesParams,
	ListRentalNotesResponse,
	CreateRentalNoteBody,
	ListRentalTasksParams,
	ListRentalTasksResponse,
	CreateRentalTaskBody,
	UpdateRentalTaskBody,
	RentalMessage,
	RentalMessageChannel,
	RentalWritableChannel,
	RentalMessageDirection,
	ListRentalMessagesParams,
	ListRentalMessagesResponse,
	CreateRentalMessageBody,
	ListRentalHistoryParams,
	ListRentalHistoryResponse,
	UpsertAutomationBody,
	AutomationUpsertInput,
	// finance / invoices
	Invoice,
	InvoiceItem,
	InvoiceType,
	InvoiceStatus,
	PaymentMethodType,
	FinanceMetrics,
	FinanceMetricsParams,
	FinanceTransaction,
	ListInvoicesParams,
	ListInvoicesResponse,
	ListRentalInvoicesParams,
	CreateInvoiceBody,
	UpdateInvoiceBody,
	ListFinanceTransactionsParams,
	ListFinanceTransactionsResponse,
	CreateTransactionBody,
	// document
	RegistryDocument,
	DocumentStatus,
	DocumentType,
	DocumentEntityType,
	SigningMethod,
	TemplateBlock,
	TemplateBlockType,
	TemplateFieldType,
	DocumentTemplate,
	DocumentTemplateVersion,
	ListRegistryDocumentsParams,
	ListRegistryDocumentsResponse,
	CreateDocumentBody,
	UpdateDocumentBody,
	UpdateDocumentStatusBody,
	ListEntityDocumentsParams,
	ListEntityDocumentsResponse,
	ListDocumentTemplatesParams,
	ListDocumentTemplatesResponse,
	CreateDocumentTemplateBody,
	UpdateDocumentTemplateBody,
	DocumentSignature,
	ListSignaturesResponse,
	CreateSignatureBody,
	// integration
	IntegrationPlatform,
	PlatformConnection,
	ExternalListing,
	ExternalListingStatus,
	ListExternalListingsParams,
	ListExternalListingsResponse,
	ConnectPlatformBody,
	PublishListingBody,
	AvitoOauthUrlResponse,
	CompleteAvitoOauthBody,
	GetAvitoOauthUrlParams,
	PublicationImport,
	// dashboard
	DashboardTask,
	DashboardTaskKind,
	DashboardTaskPriority,
	DashboardTaskEntityType,
	DashboardTaskEntityRef,
	ListDashboardTasksParams,
	ListDashboardTasksResponse,
	DashboardEvent,
	DashboardEventKind,
	ListDashboardEventsParams,
	ListDashboardEventsResponse,
	OnboardingStatus,
	ClientFlowMetrics,
	ClientFlowSeriesPoint,
	ClientFlowSeriesParams,
	ClientFlowSeriesResponse,
	PortfolioBreakdown,
	PublicationMetric,
	PublicationPeriod,
	PublicationStats,
	PublicationPlatform,
	Recommendation,
	// tasks (unified — backend gap, see project_dto_gaps_pending.md)
	Task,
	TaskKind,
	TaskPriority,
	TaskStatus,
	TaskBindingKind,
	TaskBinding,
	TaskSortKey,
	ListTasksParams,
	ListTasksResponse,
	CreateTaskBody,
	UpdateTaskBody,
	// common
	OkResponse,
	PaginationParams,
	SortParams,
	CrmListResponse,
	CurrencyCode,
} from '@rentflowdev/api'

export { ACCOUNT_TYPES, ACCOUNT_ROLES, CURRENCIES, ORGANIZATION_SEGMENTS, SIGNATORY_BASIS, SUB_ACCOUNT_STATUSES, TAX_REGIMES, USN_INCOME_BANDS } from '@rentflowdev/api'

import type {
	RentalStatus,
	PaymentStatus,
	RentalPeriodicity,
	DealStage,
	DealStatus,
	DealMeetingStatus,
	DocumentStatus,
	DocumentType,
	InvoiceStatus,
	InvoiceType,
	PaymentMethodType,
	PropertyType,
	PropertyStatus,
	UtilityType,
	UtilityCalculation,
	UtilityPaidBy,
	MessageChannel,
	RentalTaskType,
} from '@rentflowdev/api'

// ── UI-only types ─────────────────────────────────────────

export interface TokenResponse {
	access_token: string
	refresh_token: string
	token_type: string
	expires_in: number
	scope: string
}

export interface Manager {
	id: string
	name: string
	avatar: string
}

/** Legacy display shape for property listing publications panel. */
export interface Publication {
	platform: string
	status: 'PUBLISHED' | 'UNPUBLISHED' | 'MODERATION' | 'PAUSED'
	favorites?: number
	views?: number
	logoUrl?: string
}

/** Frontend-only filter shape used by property pages. */
export interface PropertyFilters {
	type: PropertyType | ''
	status: PropertyStatus | ''
	manager: string
	platform: string
	/** "Published" isn't a PropertyStatus — it's derived from ExternalListing
	 *  existence, so it's a separate filter axis, not a status value. */
	published: 'PUBLISHED' | 'UNPUBLISHED' | ''
	search: string
}

/** Frontend-only filter shape used by deal pages. */
export interface DealFilters {
	property: string
	client: string
	source: string
	search: string
}

/** Reasons for rentals appearing as incomplete (frontend-only display state). */
export type IncompleteReason =
	| 'AWAITING_PAYMENT'
	| 'CONTRACT_EXPIRED'
	| 'PENALTIES_CHARGED'

// ── UI labels & key maps ──────────────────────────────────

// ── Integration platform helpers ──────────────────────────
export type Platform = 'AVITO' | 'CIAN' | 'SITE'
export type ListingStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'MODERATION' | 'PAUSED' | 'ERROR'

/** Types the gateway currently accepts when creating a property (gateway DTO: PropertyTypeDto). */
export type CreatePropertyType = PropertyType

/**
 * Типы в порядке показа. Подписи берутся из `properties.types.*` там, где
 * рисуются: раньше рядом лежали русские строки, и при смене языка они
 * оставались русскими — единственное место в приложении, где i18n обходили.
 */
export const CREATE_PROPERTY_TYPES: CreatePropertyType[] = [
	'APARTMENT', 'HOUSE', 'ROOM', 'OFFICE', 'LAND',
	'RETAIL', 'WAREHOUSE', 'GARAGE', 'COWORKING',
]

// ── Rentals ───────────────────────────────────────────────

export const RENTAL_STATUSES: RentalStatus[] = [
	'ACTIVE',
	'UPCOMING',
	'CLOSING',
	'COMPLETED',
]
export const PAYMENT_STATUSES: PaymentStatus[] = [
	'OVERDUE',
	'PENDING',
	'PAID',
]
export const INCOMPLETE_REASONS: IncompleteReason[] = [
	'AWAITING_PAYMENT',
	'CONTRACT_EXPIRED',
	'PENALTIES_CHARGED',
]
export const RENTAL_PERIODS: RentalPeriodicity[] = [
	'MONTHLY',
	'WEEKLY',
	'QUARTERLY',
	'YEARLY',
]
/** Fallback only — the account's own currency is what forms should use.
 * Re-exported from the same module the backend validates against. */
export { DEFAULT_CURRENCY } from '@rentflowdev/common/currency'
export const DEFAULT_LOCALE = 'ru-RU'

export const CURRENCY_SIGNS: Record<string, string> = {
	RUB: '₽',
	USD: '$',
	EUR: '€',
	KZT: '₸',
	BYN: 'Br',
}

export const RENTAL_STATUS_KEYS: Record<RentalStatus, string> = {
	ACTIVE:     'rent.active.tab',
	UPCOMING:   'rent.upcoming.tab',
	CLOSING:    'rent.closing.tab',
	COMPLETED:  'rent.completed.tab',
}

export const PAYMENT_STATUS_KEYS: Record<PaymentStatus, string> = {
	OVERDUE: 'rent.paymentStatuses.overdue',
	PENDING: 'rent.paymentStatuses.pending',
	PAID:    'rent.paymentStatuses.paid',
}

export const PAYMENT_STATUS_VARIANTS: Record<PaymentStatus, 'destructive' | 'warning' | 'success'> = {
	OVERDUE: 'destructive',
	PENDING: 'warning',
	PAID:    'success',
}

export const INCOMPLETE_REASON_KEYS: Record<IncompleteReason, string> = {
	AWAITING_PAYMENT:  'rent.incompleteReasons.awaitingPayment',
	CONTRACT_EXPIRED:  'rent.incompleteReasons.contractExpired',
	PENALTIES_CHARGED: 'rent.incompleteReasons.penaltiesCharged',
}

export const INCOMPLETE_REASON_VARIANTS: Record<IncompleteReason, 'destructive' | 'warning'> = {
	AWAITING_PAYMENT:  'destructive',
	CONTRACT_EXPIRED:  'warning',
	PENALTIES_CHARGED: 'destructive',
}

// ── Documents ─────────────────────────────────────────────

export const DOCUMENT_TYPES: DocumentType[] = ['LEASE_CONTRACT', 'ACT_TRANSFER', 'ACT_RETURN', 'APPENDIX', 'REGISTRATION_APPLICATION', 'OTHER']
export const DOCUMENT_STATUSES: DocumentStatus[] = ['DRAFT', 'DATA_FILLED', 'SIGNED_BY_ME', 'SENT', 'SIGNED_BY_TENANT', 'SIGNED_BY_BOTH']

export const DOCUMENT_TYPE_KEYS: Record<DocumentType, string> = {
	LEASE_CONTRACT: 'rent.documents.types.leaseContract',
	ACT_TRANSFER:   'rent.documents.types.actTransfer',
	ACT_RETURN:     'rent.documents.types.actReturn',
	APPENDIX:       'rent.documents.types.appendix',
	REGISTRATION_APPLICATION: 'rent.documents.types.registrationApplication',
	OTHER:          'rent.documents.types.other',
}

export const DOCUMENT_STATUS_KEYS: Record<DocumentStatus, string> = {
	DRAFT:            'rent.documents.statuses.draft',
	DATA_FILLED:      'rent.documents.statuses.dataFilled',
	SIGNED_BY_ME:     'rent.documents.statuses.signedByMe',
	SENT:             'rent.documents.statuses.sent',
	SIGNED_BY_TENANT: 'rent.documents.statuses.signedByTenant',
	SIGNED_BY_BOTH:   'rent.documents.statuses.signedByBoth',
}

export const DOCUMENT_STATUS_VARIANTS: Record<DocumentStatus, 'secondary' | 'info' | 'warning' | 'success'> = {
	DRAFT:            'secondary',
	DATA_FILLED:      'success',
	SIGNED_BY_ME:     'info',
	SENT:             'warning',
	SIGNED_BY_TENANT: 'success',
	SIGNED_BY_BOTH:   'secondary',
}

// ── Utilities ─────────────────────────────────────────────

export const UTILITY_TYPE_KEYS: Record<UtilityType, string> = {
	ELECTRICITY: 'rent.terms.utilities.electricity',
	WATER:       'rent.terms.utilities.water',
	HEATING:     'rent.terms.utilities.heating',
	WASTE:       'rent.terms.utilities.waste',
	GAS:         'rent.terms.utilities.gas',
	INTERNET:    'rent.terms.utilities.internet',
	OTHER:       'rent.terms.utilities.other',
}

export const UTILITY_CALCULATION_KEYS: Record<UtilityCalculation, string> = {
	BY_METER:      'rent.terms.calc.byMeter',
	PROPORTIONAL:  'rent.terms.calc.proportional',
	FIXED_PER_SQM: 'rent.terms.calc.fixedPerSqm',
	FIXED_AMOUNT:  'rent.terms.calc.fixedAmount',
	COEFFICIENT:   'rent.terms.calc.coefficient',
	INCLUDED:      'rent.terms.calc.included',
}

export const UTILITY_PAID_BY_KEYS: Record<UtilityPaidBy, string> = {
	TENANT:   'rent.terms.paidBy.tenant',
	LANDLORD: 'rent.terms.paidBy.landlord',
}

// ── Invoices ──────────────────────────────────────────────

export const INVOICE_TYPES: InvoiceType[] = ['RENT', 'DEPOSIT', 'UTILITIES', 'PENALTY', 'ADDITIONAL', 'ONE_TIME']
export const INVOICE_STATUSES: InvoiceStatus[] = ['PENDING', 'PAID', 'OVERDUE', 'CANCELLED', 'ADJUSTED', 'PARTIAL']

export const INVOICE_TYPE_KEYS: Record<InvoiceType, string> = {
	RENT:       'rent.invoices.types.rent',
	DEPOSIT:    'rent.invoices.types.deposit',
	UTILITIES:  'rent.invoices.types.utilities',
	PENALTY:    'rent.invoices.types.penalty',
	ADDITIONAL: 'rent.invoices.types.additional',
	ONE_TIME:   'rent.invoices.types.oneTime',
}

export const INVOICE_STATUS_KEYS: Record<InvoiceStatus, string> = {
	PENDING:   'rent.invoices.statuses.pending',
	PAID:      'rent.invoices.statuses.paid',
	OVERDUE:   'rent.invoices.statuses.overdue',
	CANCELLED: 'rent.invoices.statuses.cancelled',
	ADJUSTED:  'rent.invoices.statuses.adjusted',
	PARTIAL:   'rent.invoices.statuses.partial',
}

export const INVOICE_STATUS_VARIANTS: Record<InvoiceStatus, 'warning' | 'success' | 'destructive' | 'secondary' | 'default'> = {
	PENDING:   'warning',
	PAID:      'success',
	OVERDUE:   'destructive',
	CANCELLED: 'secondary',
	ADJUSTED:  'default',
	PARTIAL:   'warning',
}

export const PAYMENT_METHOD_KEYS: Record<PaymentMethodType, string> = {
	KASSA: 'finances.paymentMethods.kassa',
	CASH: 'finances.paymentMethods.cash',
	BANK_TRANSFER: 'finances.paymentMethods.bankTransfer',
	CARD: 'finances.paymentMethods.card',
}

// ── Rental tasks ──────────────────────────────────────────

export const RENTAL_TASK_TYPES: RentalTaskType[] = ['CALL', 'MEETING', 'EMAIL', 'OTHER']

export const RENTAL_TASK_TYPE_KEYS: Record<RentalTaskType, string> = {
	CALL:    'rent.tasks.types.call',
	MEETING: 'rent.tasks.types.meeting',
	EMAIL:   'rent.tasks.types.email',
	OTHER:   'rent.tasks.types.other',
}

// ── Deal Meetings ─────────────────────────────────────────

export const DEAL_MEETING_STATUSES: DealMeetingStatus[] = ['SCHEDULED', 'CONFIRMED', 'COMPLETED', 'CANCELLED']

export const DEAL_MEETING_STATUS_KEYS: Record<DealMeetingStatus, string> = {
	SCHEDULED: 'deals.detail.meetingStatuses.scheduled',
	CONFIRMED: 'deals.detail.meetingStatuses.confirmed',
	COMPLETED: 'deals.detail.meetingStatuses.completed',
	CANCELLED: 'deals.detail.meetingStatuses.cancelled',
}

export const DEAL_MEETING_STATUS_VARIANTS: Record<DealMeetingStatus, 'warning' | 'info' | 'success' | 'destructive'> = {
	SCHEDULED: 'warning',
	CONFIRMED: 'info',
	COMPLETED: 'success',
	CANCELLED: 'destructive',
}

// ── Deal stages ───────────────────────────────────────────

/** Stages still considered active in the kanban / pipeline view. */
export const ACTIVE_STAGES: DealStage[] = ['NEW', 'CONTACT', 'NEGOTIATION', 'VIEWING', 'CONTRACT']

export const DEAL_STAGE_KEYS: Record<DealStage, string> = {
	NEW:         'deals.stages.new',
	CONTACT:     'deals.stages.contact',
	NEGOTIATION: 'deals.stages.negotiation',
	VIEWING:     'deals.stages.meeting',
	CONTRACT:    'deals.stages.contract',
	ACTIVE:      'deals.stages.contract',
	CLOSED:      'deals.stages.contract',
	CANCELLED:   'deals.stages.contract',
}

export const DEAL_STATUSES: DealStatus[] = ['ACTIVE', 'COMPLETED', 'CANCELLED']

/**
 * A deal finished through the rental flow is stored as CLOSED, which is not a
 * selectable status — a Select bound straight to `deal.status` renders empty
 * for every completed deal. CLOSED and COMPLETED mean the same thing to the
 * user, so map it onto the option that exists.
 */
export function dealStatusForSelect(status: DealStatus): DealStatus {
	return status === 'CLOSED' ? 'COMPLETED' : status
}

export const DEAL_STATUS_KEYS: Record<DealStatus, string> = {
	ACTIVE:    'deals.dealStatuses.active',
	COMPLETED: 'deals.dealStatuses.completed',
	CLOSED:    'deals.dealStatuses.completed',
	CANCELLED: 'deals.dealStatuses.cancelled',
}

export const DEAL_STATUS_VARIANTS: Record<DealStatus, 'success' | 'warning' | 'destructive'> = {
	ACTIVE:    'warning',
	COMPLETED: 'success',
	CLOSED:    'success',
	CANCELLED: 'destructive',
}

// ── Messages ──────────────────────────────────────────────

// Labels live in platforms.json under `messageChannels`, not here: a channel
// name is user-facing text, and a second copy in code drifts from the locale.
export const MESSAGE_CHANNELS: MessageChannel[] = ['AVITO', 'CIAN', 'TELEGRAM', 'WHATSAPP', 'EMAIL']
